import React, { useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import {
  Button,
  StyledText,
  RateBoxContainer,
} from "../../../../components/common";
import {
  NewBackButton,
  RadioButtonCheckedIcon,
  RadioButtonUncheckedIcon,
} from "../../../../components/svgs";
import SurveyCompletonModal from "../components/SurveyCompletonModal";
import { useTheme } from "../../../../hooks";
import { loyaltySurveyQuestions } from "../components/surveyQuestions";
import LeaveSurveyModal from "../components/LeaveSurveyModal";

const LoyaltySurvey = () => {
  const [responses, setResponses] = useState({});
  const [selectedRate, setSelectedRate] = useState({});
  const [selectedChoice, setSelectedChoice] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLeaveSurveyModalOpen, setIsLeaveSurveyModalOpen] = useState(false);

  const openLeaveSurveyModal = () => setIsLeaveSurveyModalOpen(true);
  const closeLeaveSurveyModal = () => setIsLeaveSurveyModalOpen(false);

  const history = useHistory();
  const { themeContext, theme } = useTheme();
  let themeColor = "background";
  let background = theme.colors[themeContext.activeTheme][themeColor];

  const handleInputChange = (e, responseKey) => {
    setResponses({ ...responses, [responseKey]: e.target.value });
  };

  const handleRateChange = (value, responseKey) => {
    setSelectedRate({ ...selectedRate, [responseKey]: value });
    setResponses({ ...responses, [responseKey]: value });
  };

  const handleMultipleChoiceChange = (optionIndex, responseKey, optionText) => {
    setSelectedChoice({ ...selectedChoice, [responseKey]: optionIndex });
    setResponses({ ...responses, [responseKey]: optionText });
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleSubmit = () => {
    console.log("Survey Responses: ", responses);
    openModal();
  };

  return (
    <>
      <MainHolder>
        <Children>
          <Wrapper>
            <HeaderContainer>
              <BackButtonContainer onClick={openLeaveSurveyModal}>
                <NewBackButton />
              </BackButtonContainer>
              <StyledText size="20">Loyalty Survey</StyledText>
            </HeaderContainer>

            <ThankYouCard>
              <StyledText>Thank you for taking part</StyledText>
              <StyledDescription>
                Please complete this survey to help us improve.
              </StyledDescription>
            </ThankYouCard>

            <QuestionContainer>
              {loyaltySurveyQuestions.map((question, index) => (
                <div key={index}>
                  <StyledText>{question.questionText}</StyledText>

                  {/* Text Area Question */}
                  {question.type === "textarea" && (
                    <StyledTextArea
                      value={responses[question.questionText]}
                      onChange={(e) =>
                        handleInputChange(e, question.questionText)
                      }
                      rows={4}
                    />
                  )}

                  {/* Rate Question */}
                  {question.type === "rate" && (
                    <>
                      {question.description && (
                        <QuoteSpan>{question.description}</QuoteSpan>
                      )}
                      <RateBoxContainer>
                        {Array.from({ length: 10 }, (_, i) => (
                          <RateBox
                            key={i}
                            onClick={() =>
                              handleRateChange(i + 1, question.questionText)
                            }
                            isSelected={
                              selectedRate[question.questionText] === i + 1
                            }
                          >
                            <QuoteSpan>{i + 1}</QuoteSpan>
                          </RateBox>
                        ))}
                      </RateBoxContainer>
                    </>
                  )}

                  {/* Multiple Choice Question */}
                  {question.type === "multiple-choice" && (
                    <MultipleChoiceContainer>
                      {question.options.map((option, optionIndex) => (
                        <MultipleChoiceAnswer
                          key={optionIndex}
                          onClick={() =>
                            handleMultipleChoiceChange(
                              optionIndex,
                              question.questionText,
                              option
                            )
                          }
                          isSelected={
                            selectedChoice[question.questionText] ===
                            optionIndex
                          }
                        >
                          {selectedChoice[question.questionText] ===
                          optionIndex ? (
                            <RadioButtonCheckedIcon
                              width="20"
                              height="20"
                              fill="black"
                            />
                          ) : (
                            <RadioButtonUncheckedIcon
                              width="20"
                              height="20"
                              fill="black"
                            />
                          )}
                          {option}
                        </MultipleChoiceAnswer>
                      ))}
                    </MultipleChoiceContainer>
                  )}
                </div>
              ))}
            </QuestionContainer>

            <ButtonContainer>
              <Button
                style={{ backgroundColor: background, width: "100%" }}
                onClick={handleSubmit}
              >
                <StyledText color="white">Submit</StyledText>
              </Button>
            </ButtonContainer>
          </Wrapper>
        </Children>
      </MainHolder>
      <SurveyCompletonModal isOpen={isModalOpen} onClose={closeModal} />
      <LeaveSurveyModal
        isOpen={isLeaveSurveyModalOpen}
        onClose={closeLeaveSurveyModal}
      />
    </>
  );
};

export default LoyaltySurvey;

// Styled Components
const Wrapper = styled.div`
  margin: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  background-color: #fff;
`;

const RateBox = styled.div`
  width: 48px;
  height: 22px;
  border-radius: 4px;
  border: 1px solid gray;
  background-color: ${({ isSelected }) => (isSelected ? "black" : "#fff")};
  color: ${({ isSelected }) => (isSelected ? "#fff" : "black")};
  margin: 0;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background-color: ${({ isSelected }) => (isSelected ? "gray" : "#f2f2f2")};
    color: ${({ isSelected }) => (isSelected ? "#fff" : "black")};
  }
`;

const MultipleChoiceAnswer = styled(StyledText)`
  display: flex;
  align-items: center;
  gap: 5px;
  border-radius: 4px;
  cursor: pointer;
  color: black;
  padding: 5px;
  margin: 5px 0;
  font-family: "MTNRegular";

  &:hover {
    background-color: #f2f2f2;
    color: black;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  resize: none;
`;

const MainHolder = styled.div`
  overflow: hidden;
  background-color: white;
  height: 100vh; /* Full viewport height */
  display: flex;
  flex-direction: column;
  position: relative;
  @media screen and (max-width: 480px) {
    border-radius: 0px;
    padding-bottom: 5px;
    height: 100vh;
  }
`;

const Children = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MultipleChoiceContainer = styled.div``;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 25%;
  top: 0;
  width: 100%;
`;

const ThankYouCard = styled.div`
  width: 90%;
  margin: 20px 0;
  padding: 0 18px 20px 10px;
  background: #f2f2f2;
`;

const QuestionContainer = styled.div`
  width: 90%;
  margin-bottom: 20px;
  padding: 5px;
`;

const BackButtonContainer = styled.div`
  background-color: #ffcc00;
  width: 30px;
  height: 30px;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin: 20px 0;
`;

const StyledDescription = styled(StyledText)`
  margin: 0;
  font-family: "MTNRegular";
  font-size: 11px;
`;

const QuoteSpan = styled.span`
  font-family: "MTNRegular";
  font-size: 12px;
`;
